import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { LayoutGrid, MessageSquare, UserCircle, Map, Activity, FolderOpen, Monitor, ChevronDown, Bookmark, Plus, Settings, Paperclip, Send, X, Search, Clock, FileText, Folder, Play, Pause, CheckCircle2, Sparkles, ArrowRight, ChevronLeft, ListChecks, Globe, Loader2 } from 'lucide-react';
// ── Starter prompt categories & data ────────────────────────────────────────
type StarterCategory = 'comms' | 'operations' | 'admin' | 'growth' | 'insights';
type PlanSite = {
  slug: string;
  label: string;
};
type StarterPrompt = {
  title: string;
  desc: string;
  tools: string[];
  // The domains/tools the plan will act on, shown under "Allow actions on…".
  sites: PlanSite[];
  // The step-by-step approach Giga proposes before acting.
  steps: string[];
};
const STARTER_CATEGORIES: {
  id: StarterCategory;
  label: string;
}[] = [{
  id: 'comms',
  label: 'Comms'
}, {
  id: 'operations',
  label: 'Operations'
}, {
  id: 'admin',
  label: 'Admin'
}, {
  id: 'growth',
  label: 'Growth'
}, {
  id: 'insights',
  label: 'Insights'
}];
const STARTER_PROMPTS: Record<StarterCategory, StarterPrompt[]> = {
  comms: [{
    title: 'Meeting recaps',
    desc: 'When a meeting transcript lands, create tasks for action items and email a recap to attendees.',
    tools: ['gmail', 'asana', 'slack'],
    sites: [{
      slug: 'asana',
      label: 'Asana'
    }, {
      slug: 'gmail',
      label: 'Gmail'
    }, {
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ['Pull the latest meeting transcript and identify the action items', 'Create an Asana task for each action item with its owner and due date', 'Draft a concise recap summarizing decisions and next steps', 'Email the recap to every attendee from Gmail', 'Post a short summary to the relevant Slack channel']
  }, {
    title: 'Slack reply nudges',
    desc: "When I'm mentioned in a thread but don't reply within 2 hours, text me a reminder with context.",
    tools: ['slack'],
    sites: [{
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ['Watch every Slack thread where you are @-mentioned', 'Start a 2-hour timer the moment a mention lands', 'Check whether you have replied before the timer runs out', 'If not, summarize who asked and what they need', 'Send you a reminder with a direct link to the thread']
  }, {
    title: 'Daily briefing',
    desc: 'Send me a morning briefing pulled from my Calendar, Gmail, and open Asana tasks.',
    tools: ['google', 'gmail', 'asana'],
    sites: [{
      slug: 'google',
      label: 'Google Calendar'
    }, {
      slug: 'gmail',
      label: 'Gmail'
    }, {
      slug: 'asana',
      label: 'Asana'
    }],
    steps: ["Pull today's events from Google Calendar", 'Scan Gmail for unread or flagged messages that need a reply', 'List Asana tasks due today or overdue', 'Compose a single, scannable morning briefing', 'Deliver it to you at 8:00am each weekday']
  }],
  operations: [{
    title: 'Protect focus blocks',
    desc: 'On a fragmented day, see if meetings can shift to open up focus blocks.',
    tools: ['google'],
    sites: [{
      slug: 'google',
      label: 'Google Calendar'
    }],
    steps: ["Review today's calendar for fragmentation and gaps", 'Identify meetings that could be moved without conflicts', 'Find at least one 90-minute focus block to protect', 'Draft reschedule proposals for the movable meetings', 'Ask for your approval before sending any invites']
  }, {
    title: 'Bug triage pipeline',
    desc: 'When a Sentry error spikes, open a GitHub issue and post the details to Slack.',
    tools: ['sentry', 'github', 'slack'],
    sites: [{
      slug: 'sentry',
      label: 'Sentry'
    }, {
      slug: 'github',
      label: 'GitHub'
    }, {
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ['Detect when a Sentry error crosses its spike threshold', 'Gather the stack trace, affected users, and release info', 'Open a GitHub issue with the details and a triage label', 'Post an alert with the issue link to the #engineering channel', 'Tag the most likely owner based on recent commits']
  }, {
    title: 'Stale issue sweep',
    desc: 'Each week, find Linear issues with no activity in 14 days and ping their owners.',
    tools: ['linear', 'slack'],
    sites: [{
      slug: 'linear',
      label: 'Linear'
    }, {
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ['Query Linear for issues with no activity in the last 14 days', 'Group the stale issues by assignee', 'Draft a friendly nudge for each owner with their issue list', 'Send each nudge as a Slack DM', 'Post a summary of the sweep to the team channel']
  }],
  admin: [{
    title: 'Friday inbox cleanup',
    desc: 'Every Friday, archive non-actionable emails and flag anything that needs a reply.',
    tools: ['gmail'],
    sites: [{
      slug: 'gmail',
      label: 'Gmail'
    }],
    steps: ["Scan this week's Gmail inbox and classify each message", 'Archive newsletters and notifications that need no action', 'Flag messages that still need a reply from you', 'Draft quick replies for the most routine threads', 'Send you a short summary of what was cleaned up']
  }, {
    title: 'Invoice reconciliation',
    desc: 'When a payment lands in Stripe, match it to an invoice and update the books.',
    tools: ['stripe', 'notion'],
    sites: [{
      slug: 'stripe',
      label: 'Stripe'
    }, {
      slug: 'notion',
      label: 'Notion'
    }],
    steps: ['Detect the new payment as it lands in Stripe', 'Match the amount and customer to an open invoice', 'Mark the matched invoice as paid', 'Update the bookkeeping ledger in Notion', 'Flag any payment that has no matching invoice']
  }, {
    title: 'Document filing',
    desc: 'Sort new files into the right Drive folders and rename them to our convention.',
    tools: ['google'],
    sites: [{
      slug: 'google',
      label: 'Google Drive'
    }],
    steps: ['Watch the Drive inbox folder for newly added files', 'Read each file to infer its type and project', 'Rename it to match the team naming convention', 'Move it into the correct project folder', 'Log anything ambiguous for you to confirm']
  }],
  growth: [{
    title: 'Ad spend audit',
    desc: 'Pause ad sets under 0.8x ROAS in Meta Ads and export a summary PDF.',
    tools: ['meta', 'notion'],
    sites: [{
      slug: 'meta',
      label: 'Meta Ads'
    }, {
      slug: 'notion',
      label: 'Notion'
    }],
    steps: ['Pull the last 14 days of spend and ROAS from Meta Ads', 'Flag every ad set performing under 0.8× ROAS', 'Pause the flagged ad sets to stop the bleed', 'Export a summary PDF of what changed and why', 'Save the report to Notion and notify you']
  }, {
    title: 'New deal onboarding',
    desc: 'When a deal closes, create the client workspace and send the welcome sequence.',
    tools: ['notion', 'gmail', 'asana'],
    sites: [{
      slug: 'notion',
      label: 'Notion'
    }, {
      slug: 'gmail',
      label: 'Gmail'
    }, {
      slug: 'asana',
      label: 'Asana'
    }],
    steps: ['Detect the closed-won deal and pull the client details', 'Spin up the client onboarding workspace in Notion', 'Create the standard onboarding tasks in Asana', 'Send the welcome email sequence from Gmail', 'Notify the account owner that onboarding has kicked off']
  }, {
    title: 'Lead enrichment',
    desc: 'Enrich new CRM contacts with firmographics and route hot ones to sales.',
    tools: ['attio', 'slack'],
    sites: [{
      slug: 'attio',
      label: 'Attio'
    }, {
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ['Watch Attio for newly added contacts', 'Enrich each one with firmographic and role data', 'Score the lead against your ideal-customer profile', 'Update the CRM record with the enriched fields', 'Route hot leads to sales in the #pipeline channel']
  }],
  insights: [{
    title: 'Weekly report generation',
    desc: 'Every Monday, pull last week metrics and post a digest to #leadership.',
    tools: ['notion', 'slack'],
    sites: [{
      slug: 'notion',
      label: 'Notion'
    }, {
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ["Pull last week's metrics from the Notion dashboards", 'Compare them against the prior week and targets', 'Highlight the biggest wins and the risks to watch', 'Compose a concise leadership digest', 'Post it to the #leadership channel every Monday at 9am']
  }, {
    title: 'Pipeline cleanup',
    desc: 'Flag stale or at-risk deals and summarize what changed since last week.',
    tools: ['attio', 'slack'],
    sites: [{
      slug: 'attio',
      label: 'Attio'
    }, {
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ['Scan the Attio pipeline for deals with no recent activity', 'Flag deals slipping past their expected close date', 'Summarize what changed on each deal since last week', 'Suggest a next step for every at-risk deal', 'Post the cleanup summary to the sales channel']
  }, {
    title: 'Support trends',
    desc: 'Cluster this week support tickets by theme and surface the top issues.',
    tools: ['crisp', 'notion'],
    sites: [{
      slug: 'crisp',
      label: 'Crisp'
    }, {
      slug: 'notion',
      label: 'Notion'
    }],
    steps: ["Pull this week's resolved and open tickets from Crisp", 'Cluster the tickets by underlying theme', 'Rank the themes by volume and customer impact', 'Write up the top three issues with example tickets', 'Save the trends report to Notion for the team']
  }]
};
// Small tool/site icon chip reused across the rail.
function ToolIcon({
  slug,
  size = 'sm'
}: {
  slug: string;
  size?: 'sm' | 'md';
}) {
  const box = size === 'md' ? 'w-6 h-6' : 'w-5 h-5';
  const img = size === 'md' ? 'w-3.5 h-3.5' : 'w-3 h-3';
  return <span className={`${box} rounded-md bg-white border border-neutral-200 flex items-center justify-center shrink-0`} data-id="element-3771">
      <img src={`https://cdn.simpleicons.org/${slug}/475569`} className={`${img} object-contain`} alt="" onError={e => {
      ;
      (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
    }} data-id="element-3772" />
    </span>;
}
// Claude-style plan flow: shows the picked task as a message, "thinks" for a
// beat, then reveals a numbered plan with the sites it will touch and
// Approve / Make changes actions.
function PlanView({
  prompt,
  onBack
}: {
  prompt: StarterPrompt;
  onBack: () => void;
}) {
  const reducedMotion = useReducedMotion();
  const [phase, setPhase] = useState<'thinking' | 'plan'>(reducedMotion ? 'plan' : 'thinking');
  const [approved, setApproved] = useState(false);
  useEffect(() => {
    if (reducedMotion) {
      setPhase('plan');
      return;
    }
    setPhase('thinking');
    const id = setTimeout(() => setPhase('plan'), 1100);
    return () => clearTimeout(id);
  }, [prompt, reducedMotion]);
  return <div className="flex flex-col" data-id="element-3773">
      {/* Back affordance */}
      <button onClick={onBack} className="self-start flex items-center gap-1 text-xs font-medium text-neutral-500 hover:text-neutral-900 transition-colors mb-4" data-id="element-3774">
        <ChevronLeft size={14} data-id="element-3775" />
        Back to suggestions
      </button>

      {/* User message bubble */}
      <div className="flex justify-end mb-5" data-id="element-3776">
        <div className="max-w-[88%] rounded-2xl rounded-tr-sm bg-[#0F172A] text-white text-sm leading-relaxed px-3.5 py-2.5" data-id="element-3777">
          {prompt.desc}
        </div>
      </div>

      {/* Working / creating plan */}
      <div className="flex items-center gap-2 text-sm text-neutral-500 mb-4" data-id="element-3778">
        {phase === 'thinking' ? <>
            <Loader2 size={15} className="text-[#EA580C] animate-spin" data-id="element-3779" />
            Creating plan…
          </> : <>
            <Sparkles size={15} className="text-[#EA580C]" data-id="element-3780" />
            Here&rsquo;s the plan
          </>}
      </div>

      <AnimatePresence data-id="element-3781">
        {phase === 'plan' && <motion.div initial={reducedMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: reducedMotion ? 0 : 0.4,
        ease: 'easeOut'
      }} className="rounded-xl border border-neutral-200 bg-white overflow-hidden" data-id="element-3782">
            {/* Plan header */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-neutral-100" data-id="element-3783">
              <ListChecks size={16} className="text-neutral-500" data-id="element-3784" />
              <span className="text-sm font-semibold text-neutral-900" data-id="element-3785">
                Giga&rsquo;s plan
              </span>
            </div>

            {/* Sites it will act on */}
            <div className="px-4 py-3 border-b border-neutral-100" data-id="element-3786">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-400 mb-2.5" data-id="element-3787">
                <Globe size={12} data-id="element-3788" />
                Allow actions on these
              </div>
              <div className="flex flex-wrap gap-1.5" data-id="element-3789">
                {prompt.sites.map(site => <span key={site.slug} className="inline-flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full border border-neutral-200 bg-neutral-50 text-xs font-medium text-neutral-700" data-id="element-3790">
                    <ToolIcon slug={site.slug} data-id="element-3791" />
                    {site.label}
                  </span>)}
              </div>
            </div>

            {/* Numbered approach */}
            <div className="px-4 py-3.5" data-id="element-3792">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400 mb-3" data-id="element-3793">
                Approach to follow
              </div>
              <ol className="flex flex-col gap-3" data-id="element-3794">
                {prompt.steps.map((step, i) => <motion.li key={i} initial={reducedMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              x: -6
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: reducedMotion ? 0 : 0.3,
              delay: reducedMotion ? 0 : 0.15 + i * 0.08
            }} className="flex items-start gap-3" data-id="element-3795">
                    <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full border border-neutral-300 flex items-center justify-center text-[11px] font-semibold text-neutral-500" data-id="element-3796">
                      {i + 1}
                    </span>
                    <span className="text-sm text-neutral-700 leading-snug" data-id="element-3797">
                      {step}
                    </span>
                  </motion.li>)}
              </ol>
            </div>

            {/* Actions */}
            <div className="px-4 pb-4 pt-1" data-id="element-3798">
              {approved ? <div className="flex items-center gap-2 px-3.5 py-3 rounded-lg bg-emerald-50 text-emerald-700 text-sm font-semibold" data-id="element-3799">
                  <CheckCircle2 size={16} data-id="element-3800" />
                  Plan approved — Giga is on it
                </div> : <div className="flex flex-col gap-2" data-id="element-3801">
                  <button onClick={() => setApproved(true)} className="w-full flex items-center justify-center gap-2 bg-[#0F172A] hover:bg-[#1E293B] text-white text-sm font-semibold py-2.5 rounded-lg transition-colors" data-id="element-3802">
                    Approve plan
                  </button>
                  <button onClick={onBack} className="w-full flex items-center justify-center gap-2 border border-neutral-200 hover:bg-neutral-50 text-neutral-700 text-sm font-semibold py-2.5 rounded-lg transition-colors" data-id="element-3803">
                    Make changes
                  </button>
                </div>}
              <p className="text-[11px] text-neutral-400 leading-relaxed mt-3" data-id="element-3804">
                Giga will only use the tools listed. You&rsquo;ll be asked
                before anything else.
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function Dashboard() {
  const [activeNav, setActiveNav] = useState('overview');
  const [starterCat, setStarterCat] = useState<StarterCategory>('comms');
  const [activePlan, setActivePlan] = useState<StarterPrompt | null>(null);
  const navItems = [{
    id: 'overview',
    label: 'Overview',
    icon: LayoutGrid
  }, {
    id: 'integrations',
    label: 'Integrations',
    icon: LayoutGrid
  }, {
    id: 'conversations',
    label: 'Conversations',
    icon: MessageSquare
  }, {
    id: 'assistants',
    label: 'Assistants',
    icon: UserCircle
  }, {
    id: 'journeys',
    label: 'Journeys',
    icon: Map
  }, {
    id: 'pulses',
    label: 'Pulses',
    icon: Activity
  }, {
    id: 'files',
    label: 'Files',
    icon: FolderOpen
  }, {
    id: 'viewer',
    label: 'Viewer',
    icon: Monitor
  }];
  const integrations = [{
    name: 'AgentMail',
    desc: 'productivity',
    icon: 'gmail',
    connected: false
  }, {
    name: 'Airtable',
    desc: 'productivity',
    icon: 'airtable',
    connected: false
  }, {
    name: 'Anthropic',
    desc: 'Claude models and the Anthropic API.',
    icon: 'anthropic',
    connected: false
  }, {
    name: 'Asana',
    desc: '1 account connected',
    icon: 'asana',
    connected: true
  }, {
    name: 'Attio',
    desc: 'sales',
    icon: 'attio',
    connected: false
  }, {
    name: 'Cloudflare',
    desc: 'infrastructure',
    icon: 'cloudflare',
    connected: false
  }, {
    name: 'Cloudflare R2',
    desc: 'S3-compatible object storage with …',
    icon: 'cloudflare',
    connected: false
  }, {
    name: 'Crisp',
    desc: '1 account connected',
    icon: 'crisp',
    connected: true
  }, {
    name: 'Custom',
    desc: 'Credentials for services without a d…',
    icon: 'box',
    connected: false
  }, {
    name: 'Expo',
    desc: '1 account connected',
    icon: 'expo',
    connected: true
  }, {
    name: 'Facebook',
    desc: 'marketing',
    icon: 'facebook',
    connected: false
  }, {
    name: 'Folk',
    desc: 'sales',
    icon: 'box',
    connected: false
  }, {
    name: 'GitHub',
    desc: '2 accounts connected',
    icon: 'github',
    connected: true
  }, {
    name: 'Google Gemini',
    desc: 'ai',
    icon: 'googlegemini',
    connected: false
  }, {
    name: 'Google Workspace',
    desc: 'Gmail, Drive, Calendar, Docs, Sheets.',
    icon: 'google',
    connected: false
  }, {
    name: 'Granola',
    desc: 'AI meeting notepad. Search transcri…',
    icon: 'box',
    connected: false
  }, {
    name: 'Growi',
    desc: '1 account connected',
    icon: 'box',
    connected: true
  }, {
    name: 'Heroku',
    desc: 'infrastructure',
    icon: 'heroku',
    connected: false
  }, {
    name: 'Linear',
    desc: 'Issue tracking and project managem…',
    icon: 'linear',
    connected: false
  }, {
    name: 'Magic Patterns',
    desc: '1 account connected',
    icon: 'box',
    connected: true
  }, {
    name: 'Meta Ads',
    desc: 'Facebook + Instagram ad managem…',
    icon: 'meta',
    connected: false
  }, {
    name: 'Notion',
    desc: 'Pages, databases, and structured d…',
    icon: 'notion',
    connected: false
  }, {
    name: 'OpenAI',
    desc: 'GPT models and the OpenAI API.',
    icon: 'openai',
    connected: false
  }, {
    name: 'OpenRouter',
    desc: 'ai',
    icon: 'box',
    connected: false
  }, {
    name: 'Postgres',
    desc: 'Open-source relational database.',
    icon: 'postgresql',
    connected: false
  }, {
    name: 'RevenueCat',
    desc: '1 account connected',
    icon: 'box',
    connected: true
  }, {
    name: 'Sentry',
    desc: '1 account connected',
    icon: 'sentry',
    connected: true
  }];
  return <div className="min-h-screen bg-[#F9FAFB] flex font-body overflow-hidden" data-id="element-3805">
      {/* Left Sidebar */}
      <div className="w-[240px] bg-white border-r border-neutral-200 flex flex-col shrink-0 h-screen" data-id="element-3806">
        {/* Workspace Switcher */}
        <div className="h-14 px-4 flex items-center justify-between border-b border-neutral-200 cursor-pointer hover:bg-neutral-50 transition-colors" data-id="element-3807">
          <div className="flex items-center gap-2.5" data-id="element-3808">
            <div className="w-6 h-6 rounded bg-[#0F172A] flex items-center justify-center text-white text-xs font-bold" data-id="element-3809">
              G
            </div>
            <span className="font-semibold text-sm text-neutral-900" data-id="element-3810">
              Giga Main
            </span>
          </div>
          <ChevronDown size={14} className="text-neutral-400" data-id="element-3811" />
        </div>

        {/* Nav Links */}
        <div className="flex-1 py-4 px-3 flex flex-col gap-1 overflow-y-auto" data-id="element-3812">
          {navItems.map(item => {
          const isActive = activeNav === item.id;
          return <button key={item.id} onClick={() => setActiveNav(item.id)} className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-[#0F172A] text-white' : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'}`} data-id="element-3813">
                <item.icon size={18} strokeWidth={isActive ? 2.5 : 2} data-id="element-3814" />
                {item.label}
              </button>;
        })}
        </div>

        {/* User Profile */}
        <div className="p-4 border-t border-neutral-200" data-id="element-3815">
          <div className="flex items-center justify-between group cursor-pointer" data-id="element-3816">
            <div className="flex items-center gap-3 min-w-0" data-id="element-3817">
              <div className="w-8 h-8 rounded-full bg-neutral-200 flex items-center justify-center text-neutral-600 font-bold text-xs shrink-0" data-id="element-3818">
                F
              </div>
              <div className="flex flex-col min-w-0" data-id="element-3819">
                <span className="text-sm font-semibold text-neutral-900 truncate" data-id="element-3820">
                  Faith Alqahtani
                </span>
                <span className="text-xs text-neutral-500 truncate" data-id="element-3821">
                  faith@example.com
                </span>
              </div>
            </div>
            <Settings size={16} className="text-neutral-400 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-3822" />
          </div>
        </div>
      </div>

      {/* Main Content Column */}
      <div className="flex-1 flex flex-col min-w-0 h-screen" data-id="element-3823">
        {/* Top Bar */}
        <header className="h-14 bg-white border-b border-neutral-200 flex items-center justify-between px-6 shrink-0" data-id="element-3824">
          <div className="flex items-center gap-4" data-id="element-3825">
            <button className="flex items-center gap-2 text-sm font-medium text-neutral-700 hover:text-neutral-900 transition-colors px-3 py-1.5 rounded-md hover:bg-neutral-100" data-id="element-3826">
              <LayoutGrid size={16} className="text-neutral-400" data-id="element-3827" />
              All Spaces
              <ChevronDown size={14} className="text-neutral-400" data-id="element-3828" />
            </button>
            <div className="w-px h-4 bg-neutral-200" data-id="element-3829" />
            <button className="text-neutral-400 hover:text-neutral-600 transition-colors" data-id="element-3830">
              <Bookmark size={18} data-id="element-3831" />
            </button>
          </div>
          <button className="flex items-center gap-2 bg-[#0F172A] hover:bg-[#1E293B] text-white px-4 py-1.5 rounded-md text-sm font-medium transition-colors" data-id="element-3832">
            <Plus size={16} data-id="element-3833" />
            New chat
          </button>
        </header>

        {/* Dashboard Content Area */}
        <main className="flex-1 p-6 md:p-8 overflow-y-auto" data-id="element-3834">
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm min-h-full p-8" data-id="element-3835">
            {/* Overview */}
            {activeNav === 'overview' && <>
                <div className="flex items-center justify-between mb-8" data-id="element-3836">
                  <h1 className="text-2xl font-bold text-neutral-900" data-id="element-3837">
                    Overview
                  </h1>
                  <div className="relative hidden md:block" data-id="element-3838">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={16} data-id="element-3839" />
                    <input type="text" placeholder="Search activity..." className="pl-9 pr-4 py-2 rounded-lg border border-neutral-200 bg-neutral-50 text-sm focus:outline-none focus:border-neutral-300 focus:bg-white transition-colors w-64" data-id="element-3840" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-10" data-id="element-3841">
                  <div className="bg-neutral-50 p-6 rounded-xl border border-neutral-100" data-id="element-3842">
                    <div className="text-neutral-500 text-sm font-medium mb-2" data-id="element-3843">
                      Tasks Completed
                    </div>
                    <div className="text-4xl font-bold text-neutral-900" data-id="element-3844">
                      14,231
                    </div>
                    <div className="mt-2 text-xs font-medium text-emerald-600 bg-emerald-50 inline-flex px-2 py-0.5 rounded-full" data-id="element-3845">
                      +12% this week
                    </div>
                  </div>
                  <div className="bg-neutral-50 p-6 rounded-xl border border-neutral-100" data-id="element-3846">
                    <div className="text-neutral-500 text-sm font-medium mb-2" data-id="element-3847">
                      Hours Saved
                    </div>
                    <div className="text-4xl font-bold text-neutral-900" data-id="element-3848">
                      342
                    </div>
                    <div className="mt-2 text-xs font-medium text-emerald-600 bg-emerald-50 inline-flex px-2 py-0.5 rounded-full" data-id="element-3849">
                      ~14 days of work
                    </div>
                  </div>
                  <div className="bg-neutral-50 p-6 rounded-xl border border-neutral-100" data-id="element-3850">
                    <div className="text-neutral-500 text-sm font-medium mb-2" data-id="element-3851">
                      Active Automations
                    </div>
                    <div className="text-4xl font-bold text-neutral-900" data-id="element-3852">
                      12
                    </div>
                    <div className="mt-2 text-xs font-medium text-neutral-500 bg-neutral-100 inline-flex px-2 py-0.5 rounded-full" data-id="element-3853">
                      Running smoothly
                    </div>
                  </div>
                </div>

                <div data-id="element-3854">
                  <h2 className="font-bold text-neutral-900 text-lg mb-4" data-id="element-3855">
                    Recent Activity
                  </h2>
                  <div className="border border-neutral-200 rounded-xl overflow-hidden" data-id="element-3856">
                    <div className="divide-y divide-neutral-100" data-id="element-3857">
                      {[{
                    task: 'Drafted Q3 renewal brief for Northwind',
                    tool: 'notion',
                    time: '10 mins ago'
                  }, {
                    task: 'Pulled account history from HubSpot',
                    tool: 'hubspot',
                    time: '12 mins ago'
                  }, {
                    task: 'Replied to support ticket #4092',
                    tool: 'zendesk',
                    time: '1 hour ago'
                  }, {
                    task: 'Summarized weekly sync meeting',
                    tool: 'slack',
                    time: '3 hours ago'
                  }, {
                    task: 'Updated 14 stale deals in CRM',
                    tool: 'salesforce',
                    time: '5 hours ago'
                  }].map((item, i) => <div key={i} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2 hover:bg-neutral-50 transition-colors cursor-pointer" data-id="element-3858">
                          <div className="flex items-center gap-4" data-id="element-3859">
                            <div className="w-10 h-10 rounded-lg bg-white border border-neutral-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-3860">
                              <img src={`https://cdn.simpleicons.org/${item.tool}/475569`} className="w-5 h-5 object-contain" alt="" data-id="element-3861" />
                            </div>
                            <span className="font-medium text-neutral-900 text-sm" data-id="element-3862">
                              {item.task}
                            </span>
                          </div>
                          <span className="text-xs font-medium text-neutral-400 sm:text-right" data-id="element-3863">
                            {item.time}
                          </span>
                        </div>)}
                    </div>
                  </div>
                </div>
              </>}

            {/* Integrations */}
            {activeNav === 'integrations' && <div className="flex flex-col" data-id="element-3864">
                <div className="flex items-center justify-between mb-6" data-id="element-3865">
                  <div className="flex items-center gap-4" data-id="element-3866">
                    <h1 className="text-2xl font-bold text-neutral-900" data-id="element-3867">
                      Integrations
                    </h1>
                    <div className="flex items-center bg-neutral-100 rounded-lg p-0.5" data-id="element-3868">
                      <button className="px-3 py-1.5 text-sm font-medium bg-white rounded-md shadow-sm text-neutral-900" data-id="element-3869">
                        App View
                      </button>
                      <button className="px-3 py-1.5 text-sm font-medium text-neutral-500 hover:text-neutral-700" data-id="element-3870">
                        List View{' '}
                        <span className="ml-1 text-neutral-400" data-id="element-3871">13</span>
                      </button>
                    </div>
                  </div>
                  <button className="flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900" data-id="element-3872">
                    <Plus size={16} data-id="element-3873" />
                    Add custom
                    <ChevronDown size={14} className="ml-0.5" data-id="element-3874" />
                  </button>
                </div>

                <div className="relative mb-6" data-id="element-3875">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={16} data-id="element-3876" />
                  <input type="text" placeholder="Search 38 integrations" className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-neutral-200 bg-white text-sm focus:outline-none focus:border-neutral-300 transition-colors" data-id="element-3877" />
                </div>

                <div className="flex items-center justify-between mb-6 border-b border-neutral-100" data-id="element-3878">
                  <div className="flex items-center gap-6" data-id="element-3879">
                    <button className="text-sm font-medium text-neutral-900 border-b-2 border-neutral-900 pb-3" data-id="element-3880">
                      All integrations{' '}
                      <span className="ml-1 text-neutral-400 font-normal" data-id="element-3881">
                        38
                      </span>
                    </button>
                    <button className="text-sm font-medium text-neutral-500 hover:text-neutral-700 pb-3" data-id="element-3882">
                      Connected{' '}
                      <span className="ml-1 text-neutral-400 font-normal" data-id="element-3883">
                        9
                      </span>
                    </button>
                  </div>
                  <button className="flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900 pb-3" data-id="element-3884">
                    Business type
                    <ChevronDown size={14} data-id="element-3885" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3886">
                  {integrations.map((integration, i) => <div key={i} className="flex items-center gap-4 p-4 rounded-xl border border-neutral-200 hover:border-neutral-300 transition-colors cursor-pointer bg-white" data-id="element-3887">
                      <div className="w-10 h-10 rounded-lg bg-neutral-50 border border-neutral-100 flex items-center justify-center shrink-0" data-id="element-3888">
                        <img src={`https://cdn.simpleicons.org/${integration.icon}/475569`} className="w-5 h-5 object-contain" alt="" onError={e => {
                    ;
                    (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
                  }} data-id="element-3889" />
                      </div>
                      <div className="flex flex-col min-w-0" data-id="element-3890">
                        <span className="font-semibold text-neutral-900 text-sm truncate" data-id="element-3891">
                          {integration.name}
                        </span>
                        <div className="flex items-center gap-1.5 mt-0.5" data-id="element-3892">
                          {integration.connected && <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" data-id="element-3893" />}
                          <span className="text-xs text-neutral-500 truncate" data-id="element-3894">
                            {integration.desc}
                          </span>
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>}

            {/* Conversations */}
            {activeNav === 'conversations' && <div className="flex flex-col" data-id="element-3895">
                <div className="flex items-center justify-between mb-6" data-id="element-3896">
                  <h1 className="text-2xl font-bold text-neutral-900" data-id="element-3897">
                    Conversations
                  </h1>
                  <div className="relative hidden md:block" data-id="element-3898">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={16} data-id="element-3899" />
                    <input type="text" placeholder="Search conversations..." className="pl-9 pr-4 py-2 rounded-lg border border-neutral-200 bg-neutral-50 text-sm focus:outline-none focus:border-neutral-300 focus:bg-white transition-colors w-64" data-id="element-3900" />
                  </div>
                </div>
                <div className="border border-neutral-200 rounded-xl overflow-hidden" data-id="element-3901">
                  <div className="divide-y divide-neutral-100" data-id="element-3902">
                    {[{
                  title: 'Q3 renewal strategy',
                  preview: 'Here is the draft brief for the Northwind renewal…',
                  time: '10 mins ago'
                }, {
                  title: 'Weekly standup recap',
                  preview: 'Summarized the engineering sync and posted to Slack.',
                  time: '1 hour ago'
                }, {
                  title: 'Ad spend audit results',
                  preview: 'Paused 3 ad sets under 0.8× ROAS and exported a PDF.',
                  time: 'Yesterday'
                }, {
                  title: 'Support ticket triage',
                  preview: 'Replied to 6 tickets and escalated #4092 to eng.',
                  time: 'Yesterday'
                }, {
                  title: 'Pipeline cleanup',
                  preview: 'Updated 14 stale deals and flagged 2 at risk.',
                  time: '2 days ago'
                }, {
                  title: 'Onboarding doc for Acme',
                  preview: 'Created the Notion onboarding doc and invited the client.',
                  time: '3 days ago'
                }].map((c, i) => <div key={i} className="px-6 py-4 flex items-center justify-between gap-4 hover:bg-neutral-50 transition-colors cursor-pointer" data-id="element-3903">
                        <div className="flex items-center gap-4 min-w-0" data-id="element-3904">
                          <div className="w-10 h-10 rounded-lg bg-neutral-50 border border-neutral-200 flex items-center justify-center shrink-0" data-id="element-3905">
                            <MessageSquare size={18} className="text-neutral-500" data-id="element-3906" />
                          </div>
                          <div className="flex flex-col min-w-0" data-id="element-3907">
                            <span className="font-semibold text-neutral-900 text-sm truncate" data-id="element-3908">
                              {c.title}
                            </span>
                            <span className="text-xs text-neutral-500 truncate" data-id="element-3909">
                              {c.preview}
                            </span>
                          </div>
                        </div>
                        <span className="text-xs font-medium text-neutral-400 shrink-0" data-id="element-3910">
                          {c.time}
                        </span>
                      </div>)}
                  </div>
                </div>
              </div>}

            {/* Assistants */}
            {activeNav === 'assistants' && <div className="flex flex-col" data-id="element-3911">
                <div className="flex items-center justify-between mb-6" data-id="element-3912">
                  <h1 className="text-2xl font-bold text-neutral-900" data-id="element-3913">
                    Assistants
                  </h1>
                  <button className="flex items-center gap-1.5 bg-[#0F172A] hover:bg-[#1E293B] text-white px-3.5 py-2 rounded-md text-sm font-medium transition-colors" data-id="element-3914">
                    <Plus size={16} data-id="element-3915" />
                    New assistant
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3916">
                  {[{
                name: 'Sales Assistant',
                desc: 'Handles outreach and CRM updates.',
                initial: 'S',
                color: 'bg-amber-100 text-amber-700',
                active: true
              }, {
                name: 'Research Bot',
                desc: 'Finds and summarizes market data.',
                initial: 'R',
                color: 'bg-emerald-100 text-emerald-700',
                active: true
              }, {
                name: 'Content Writer',
                desc: 'Drafts posts and emails in brand voice.',
                initial: 'C',
                color: 'bg-purple-100 text-purple-700',
                active: true
              }, {
                name: 'Ops Manager',
                desc: 'Monitors workflows and flags issues.',
                initial: 'O',
                color: 'bg-blue-100 text-blue-700',
                active: false
              }, {
                name: 'Finance Analyst',
                desc: 'Reconciles books and builds forecasts.',
                initial: 'F',
                color: 'bg-rose-100 text-rose-700',
                active: true
              }, {
                name: 'Design Helper',
                desc: 'Turns briefs into on-brand mockups.',
                initial: 'D',
                color: 'bg-cyan-100 text-cyan-700',
                active: false
              }].map((a, i) => <div key={i} className="p-5 rounded-xl border border-neutral-200 hover:border-neutral-300 hover:shadow-sm transition-all cursor-pointer bg-white" data-id="element-3917">
                      <div className="flex items-start justify-between mb-3" data-id="element-3918">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center font-bold text-base ${a.color}`} data-id="element-3919">
                          {a.initial}
                        </div>
                        <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${a.active ? 'text-emerald-600 bg-emerald-50' : 'text-neutral-500 bg-neutral-100'}`} data-id="element-3920">
                          {a.active ? 'Active' : 'Paused'}
                        </span>
                      </div>
                      <h3 className="font-semibold text-neutral-900 text-sm mb-1" data-id="element-3921">
                        {a.name}
                      </h3>
                      <p className="text-xs text-neutral-500 leading-relaxed" data-id="element-3922">
                        {a.desc}
                      </p>
                    </div>)}
                </div>
              </div>}

            {/* Journeys */}
            {activeNav === 'journeys' && <div className="flex flex-col" data-id="element-3923">
                <div className="flex items-center justify-between mb-6" data-id="element-3924">
                  <h1 className="text-2xl font-bold text-neutral-900" data-id="element-3925">
                    Journeys
                  </h1>
                  <button className="flex items-center gap-1.5 bg-[#0F172A] hover:bg-[#1E293B] text-white px-3.5 py-2 rounded-md text-sm font-medium transition-colors" data-id="element-3926">
                    <Plus size={16} data-id="element-3927" />
                    New journey
                  </button>
                </div>
                <div className="border border-neutral-200 rounded-xl overflow-hidden" data-id="element-3928">
                  <div className="divide-y divide-neutral-100" data-id="element-3929">
                    {[{
                  name: 'New deal onboarding',
                  trigger: 'When a deal closes in HubSpot',
                  active: true,
                  last: '12 mins ago'
                }, {
                  name: 'Meeting follow-up',
                  trigger: 'After a Zoom call ends',
                  active: true,
                  last: '1 hour ago'
                }, {
                  name: 'Weekly report generation',
                  trigger: 'Every Monday at 9:00am',
                  active: true,
                  last: '2 days ago'
                }, {
                  name: 'Bug triage pipeline',
                  trigger: 'When a Sentry error spikes',
                  active: false,
                  last: '5 days ago'
                }, {
                  name: 'Invoice reconciliation',
                  trigger: 'When a payment lands in Stripe',
                  active: true,
                  last: '6 hours ago'
                }].map((j, i) => <div key={i} className="px-6 py-4 flex items-center justify-between gap-4 hover:bg-neutral-50 transition-colors cursor-pointer" data-id="element-3930">
                        <div className="flex items-center gap-4 min-w-0" data-id="element-3931">
                          <div className="w-10 h-10 rounded-lg bg-neutral-50 border border-neutral-200 flex items-center justify-center shrink-0" data-id="element-3932">
                            <Map size={18} className="text-neutral-500" data-id="element-3933" />
                          </div>
                          <div className="flex flex-col min-w-0" data-id="element-3934">
                            <span className="font-semibold text-neutral-900 text-sm truncate" data-id="element-3935">
                              {j.name}
                            </span>
                            <span className="text-xs text-neutral-500 truncate" data-id="element-3936">
                              {j.trigger}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 shrink-0" data-id="element-3937">
                          <span className="text-xs font-medium text-neutral-400 hidden sm:block" data-id="element-3938">
                            {j.last}
                          </span>
                          <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-full ${j.active ? 'text-emerald-600 bg-emerald-50' : 'text-neutral-500 bg-neutral-100'}`} data-id="element-3939">
                            {j.active ? <Play size={10} className="fill-current" data-id="element-3940" /> : <Pause size={10} className="fill-current" data-id="element-3941" />}
                            {j.active ? 'Active' : 'Paused'}
                          </span>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>}

            {/* Pulses */}
            {activeNav === 'pulses' && <div className="flex flex-col" data-id="element-3942">
                <h1 className="text-2xl font-bold text-neutral-900 mb-6" data-id="element-3943">
                  Pulses
                </h1>
                <div className="relative pl-2" data-id="element-3944">
                  <div className="absolute left-[19px] top-2 bottom-2 w-px bg-neutral-200" data-id="element-3945" />
                  <div className="flex flex-col gap-6" data-id="element-3946">
                    {[{
                  text: 'Completed: Drafted Q3 renewal brief for Northwind',
                  time: '10 mins ago',
                  done: true
                }, {
                  text: 'Triggered: New deal detected in HubSpot',
                  time: '24 mins ago',
                  done: false
                }, {
                  text: 'Completed: Posted weekly recap to #general',
                  time: '1 hour ago',
                  done: true
                }, {
                  text: 'Triggered: Payment landed in Stripe',
                  time: '3 hours ago',
                  done: false
                }, {
                  text: 'Completed: Reconciled June P&L → report.pdf',
                  time: '5 hours ago',
                  done: true
                }].map((p, i) => <div key={i} className="flex items-start gap-4 relative" data-id="element-3947">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 z-10 ${p.done ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`} data-id="element-3948">
                          {p.done ? <CheckCircle2 size={16} data-id="element-3949" /> : <Activity size={16} data-id="element-3950" />}
                        </div>
                        <div className="flex flex-col pt-1" data-id="element-3951">
                          <span className="text-sm font-medium text-neutral-900" data-id="element-3952">
                            {p.text}
                          </span>
                          <span className="text-xs text-neutral-400 mt-0.5 flex items-center gap-1" data-id="element-3953">
                            <Clock size={11} data-id="element-3954" />
                            {p.time}
                          </span>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>}

            {/* Files */}
            {activeNav === 'files' && <div className="flex flex-col" data-id="element-3955">
                <div className="flex items-center justify-between mb-6" data-id="element-3956">
                  <h1 className="text-2xl font-bold text-neutral-900" data-id="element-3957">Files</h1>
                  <button className="flex items-center gap-1.5 bg-[#0F172A] hover:bg-[#1E293B] text-white px-3.5 py-2 rounded-md text-sm font-medium transition-colors" data-id="element-3958">
                    <Plus size={16} data-id="element-3959" />
                    Upload
                  </button>
                </div>
                <div className="border border-neutral-200 rounded-xl overflow-hidden" data-id="element-3960">
                  <div className="grid grid-cols-[1fr_auto_auto] gap-4 px-6 py-3 bg-neutral-50 border-b border-neutral-200 text-[11px] font-bold uppercase tracking-wide text-neutral-400" data-id="element-3961">
                    <span data-id="element-3962">Name</span>
                    <span className="hidden sm:block w-20 text-right" data-id="element-3963">
                      Size
                    </span>
                    <span className="w-28 text-right" data-id="element-3964">Modified</span>
                  </div>
                  <div className="divide-y divide-neutral-100" data-id="element-3965">
                    {[{
                  name: 'Reports',
                  type: 'folder',
                  size: '—',
                  modified: 'Jun 14'
                }, {
                  name: 'Exports',
                  type: 'folder',
                  size: '—',
                  modified: 'Jun 12'
                }, {
                  name: 'meeting-notes-jun-12.pdf',
                  type: 'file',
                  size: '184 KB',
                  modified: 'Jun 12'
                }, {
                  name: 'q3-forecast.xlsx',
                  type: 'file',
                  size: '52 KB',
                  modified: 'Jun 10'
                }, {
                  name: 'brand-guidelines.pdf',
                  type: 'file',
                  size: '3.1 MB',
                  modified: 'Jun 8'
                }, {
                  name: 'invoices-184.zip',
                  type: 'file',
                  size: '8.4 MB',
                  modified: 'Jun 6'
                }].map((f, i) => <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-4 px-6 py-3.5 items-center hover:bg-neutral-50 transition-colors cursor-pointer" data-id="element-3966">
                        <div className="flex items-center gap-3 min-w-0" data-id="element-3967">
                          <div className="w-8 h-8 rounded-lg bg-neutral-50 border border-neutral-100 flex items-center justify-center shrink-0" data-id="element-3968">
                            {f.type === 'folder' ? <Folder size={16} className="text-neutral-400 fill-neutral-200" data-id="element-3969" /> : <FileText size={16} className="text-neutral-400" data-id="element-3970" />}
                          </div>
                          <span className="text-sm font-medium text-neutral-900 truncate" data-id="element-3971">
                            {f.name}
                          </span>
                        </div>
                        <span className="hidden sm:block w-20 text-right text-xs text-neutral-500" data-id="element-3972">
                          {f.size}
                        </span>
                        <span className="w-28 text-right text-xs text-neutral-400" data-id="element-3973">
                          {f.modified}
                        </span>
                      </div>)}
                  </div>
                </div>
              </div>}

            {/* Viewer */}
            {activeNav === 'viewer' && <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center" data-id="element-3974">
                <div className="w-16 h-16 bg-neutral-100 rounded-2xl flex items-center justify-center mb-4" data-id="element-3975">
                  <Monitor size={26} className="text-neutral-400" data-id="element-3976" />
                </div>
                <h2 className="text-xl font-bold text-neutral-900 mb-2" data-id="element-3977">
                  Nothing to preview yet
                </h2>
                <p className="text-neutral-500 max-w-sm text-sm leading-relaxed" data-id="element-3978">
                  Select a file or conversation and it will open here for a
                  full-screen preview.
                </p>
              </div>}
          </div>
        </main>
      </div>

      {/* Right Chat Panel */}
      <div className="w-[320px] lg:w-[380px] bg-white border-l border-neutral-200 flex flex-col shrink-0 h-screen hidden md:flex" data-id="element-3979">
        {/* Chat Tabs */}
        <div className="h-14 flex items-end px-2 border-b border-neutral-200 bg-neutral-50/50" data-id="element-3980">
          <div className="bg-white border border-neutral-200 border-b-0 rounded-t-lg px-4 py-2 flex items-center gap-3 text-sm font-medium text-neutral-900 relative top-px" data-id="element-3981">
            New Chat
            <button className="text-neutral-400 hover:text-neutral-600" data-id="element-3982">
              <X size={14} data-id="element-3983" />
            </button>
          </div>
        </div>

        {/* Chat Content Area */}
        <div className="flex-1 p-4 flex flex-col overflow-y-auto" data-id="element-3984">
          {activePlan ? <PlanView prompt={activePlan} onBack={() => setActivePlan(null)} data-id="element-3985" /> : <>
              <button className="self-start flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-neutral-100 hover:bg-neutral-200 text-neutral-700 text-xs font-medium transition-colors mb-6" data-id="element-3986">
                <Plus size={14} data-id="element-3987" />
                Space
              </button>

              {/* Get moving — starter prompts based on connected tools */}
              <div className="flex items-center gap-2 mb-1.5" data-id="element-3988">
                <Sparkles size={15} className="text-[#EA580C]" data-id="element-3989" />
                <h3 className="text-sm font-semibold text-neutral-900" data-id="element-3990">
                  Not sure where to start?
                </h3>
              </div>
              <p className="text-xs text-neutral-500 leading-relaxed mb-4" data-id="element-3991">
                Here are a few things you can ask, based on the tools you’ve
                connected.
              </p>

              {/* Category tabs */}
              <div className="flex flex-wrap gap-1.5 mb-4" data-id="element-3992">
                {STARTER_CATEGORIES.map(cat => {
              const isActive = starterCat === cat.id;
              return <button key={cat.id} onClick={() => setStarterCat(cat.id)} className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${isActive ? 'bg-[#0F172A] text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`} data-id="element-3993">
                      {cat.label}
                    </button>;
            })}
              </div>

              {/* Prompt suggestions */}
              <div className="flex flex-col gap-2.5" data-id="element-3994">
                {STARTER_PROMPTS[starterCat].map((prompt, i) => <button key={i} onClick={() => setActivePlan(prompt)} className="group text-left p-3.5 rounded-xl border border-neutral-200 bg-white hover:border-neutral-300 hover:shadow-sm transition-all" data-id="element-3995">
                    <div className="flex items-center justify-between gap-2 mb-1.5" data-id="element-3996">
                      <span className="text-sm font-semibold text-neutral-900" data-id="element-3997">
                        {prompt.title}
                      </span>
                      <div className="flex items-center -space-x-1.5 shrink-0" data-id="element-3998">
                        {prompt.tools.slice(0, 3).map((tool, t) => <span key={t} className="w-5 h-5 rounded-md bg-white border border-neutral-200 flex items-center justify-center" data-id="element-3999">
                            <img src={`https://cdn.simpleicons.org/${tool}/475569`} className="w-3 h-3 object-contain" alt="" onError={e => {
                      ;
                      (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
                    }} data-id="element-4000" />
                          </span>)}
                      </div>
                    </div>
                    <p className="text-xs text-neutral-500 leading-relaxed mb-2.5" data-id="element-4001">
                      {prompt.desc}
                    </p>
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-[#EA580C] opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-4002">
                      Try it
                      <ArrowRight size={12} data-id="element-4003" />
                    </span>
                  </button>)}
              </div>
            </>}
        </div>

        {/* Chat Input */}
        <div className="p-4 border-t border-neutral-200 bg-white" data-id="element-4004">
          <div className="flex items-end gap-2" data-id="element-4005">
            <button className="p-2 text-neutral-400 hover:text-neutral-600 transition-colors shrink-0 mb-1" data-id="element-4006">
              <Paperclip size={20} data-id="element-4007" />
            </button>
            <div className="flex-1 relative" data-id="element-4008">
              <textarea placeholder="Enter your message" className="w-full border border-neutral-200 rounded-xl pl-4 pr-10 py-3 text-sm focus:outline-none focus:border-neutral-300 focus:ring-1 focus:ring-neutral-300 resize-none min-h-[44px] max-h-[120px]" rows={1} data-id="element-4009" />
            </div>
            <button className="p-2.5 bg-neutral-100 text-neutral-400 rounded-xl hover:bg-neutral-200 hover:text-neutral-600 transition-colors shrink-0 mb-1" data-id="element-4010">
              <Send size={18} data-id="element-4011" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}